import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, TrendingUp } from "lucide-react";
import { Skeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Card, Empty, PageHeader, Section, Segmented, usd } from "@/features/command/ui";
import { AccountFunnelBoard } from "@/features/accounts/AccountFunnelBoard";
import { OfferLadder } from "@/features/funnel/OfferLadder";
import { FunnelMapView } from "@/features/funnel/FunnelMap";
import {
  PLAN_MONTHLY_USD,
  computeMeasuredOdds,
  monthlyValueUsd,
  isNearTrial,
  lifetimeTotal,
  type PipelineValueInput,
} from "@/features/accounts/platform-meta";

/**
 * The Funnel page: the sequential story of the customer base.
 *
 * Three views, picked by `?view=`:
 *  - Map (default): the whiteboard funnel with live numbers and filters
 *    (features/funnel/FunnelMap.tsx).
 *  - Steps: each row is a milestone; the bar is how many accounts
 *    have EVER reached it (share of all signups, so it can never exceed
 *    100%). The right-hand figure is the one that matters for money: of the
 *    accounts that reached this step, what percentage went on to pay,
 *    measured from this workspace's real history on every load. Below it,
 *    the offer ladder (Free → DIY → event → mastermind).
 *  - Board (the default, Matthew 2026-11-04): every account in the column
 *    for its stage (AccountFunnelBoard, formerly the Pipelines page).
 *
 * The money headline uses the same numbers as the Board: real plan prices ×
 * measured conversion rates. Betas (long comp trials) are excluded from all
 * math.
 */

interface FunnelAccount extends PipelineValueInput {
  id: string;
  revenue_usd?: string | null;
  lifetime_spend_usd?: string | null;
}

type View = "map" | "steps" | "board";

const STEPS: { key: string; label: string; reached: (a: FunnelAccount) => boolean }[] = [
  { key: "signed_up", label: "Signed up", reached: () => true },
  { key: "verified", label: "Verified email", reached: (a) => !!a.email_verified_at },
  { key: "built", label: "Messaged AI", reached: (a) => !!a.first_ai_at },
  { key: "published", label: "Published a site", reached: (a) => !!a.first_published_at },
  { key: "card", label: "Added a card", reached: (a) => !!a.card_added_at },
  { key: "domain", label: "Domain live", reached: (a) => !!a.first_domain_live_at },
  { key: "trialing", label: "Started a paid plan", reached: (a) => !!(a.trial_started_at || a.first_paid_at) },
  { key: "paying", label: "Paying", reached: (a) => Number(a.revenue_usd) > 0 },
];

export function FunnelPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("view");
  const view: View = raw === "map" ? "map" : raw === "steps" ? "steps" : "board";
  const setView = (v: View) => setParams(v === "board" ? {} : { view: v }, { replace: true });
  const [summary, setSummary] = useState<string | null>(null);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Customers"
        title="Funnel"
        subtitle={view === "board"
          ? "Every account in the column for where it is now. Cards move by themselves as people reach each step."
          : view === "map"
            ? "The whole journey, from where people came from to the mastermind. Click any number to see who."
            : summary ?? "How your customers progress from signup to paying."}
        actions={
          <Segmented
            value={view}
            onChange={setView}
            options={[{ value: "board", label: "Board" }, { value: "map", label: "Map" }, { value: "steps", label: "Steps" }]}
          />
        }
      />
      {view === "map" ? (
        <FunnelMapView />
      ) : view === "board" ? (
        <div className="h-[calc(100dvh-15rem)] min-h-[460px]">
          <AccountFunnelBoard />
        </div>
      ) : (
        <StepsView onSummary={setSummary} onShowBoard={() => setView("board")} />
      )}
    </div>
  );
}

function StepsView({ onSummary, onShowBoard }: { onSummary: (s: string | null) => void; onShowBoard: () => void }) {
  const toast = useToast();
  const [accounts, setAccounts] = useState<FunnelAccount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Two fetches because the accounts list hides betas by default:
        // their comp trials are worth $0/mo, but any wallet top-ups they
        // made are REAL collected dollars and belong in lifetime profit.
        const [main, beta] = await Promise.all([
          api.get<{ accounts: FunnelAccount[] }>("/platform-accounts", { limit: 500 }),
          api.get<{ accounts: FunnelAccount[] }>("/platform-accounts", {
            limit: 500,
            stage: "beta",
          }),
        ]);
        const seen = new Set(main.accounts.map((a) => a.id));
        const merged = [
          ...main.accounts,
          ...beta.accounts.filter((a) => !seen.has(a.id)),
        ];
        if (!cancelled) setAccounts(merged);
      } catch (err) {
        if (!cancelled) toast.error(err instanceof ApiError ? err.message : "Failed to load funnel");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isBeta = (a: FunnelAccount) =>
    a.subscription_status === "trialing" && !isNearTrial(a.trial_ends_at);
  // Agency-ORG customers carry an aggregated journey (earliest milestone
  // across their workspaces), so they walk the same funnel as everyone.
  const eligible = accounts.filter((a) => !isBeta(a));
  const betas = accounts.filter(isBeta).length;

  // The header line names how many customers the steps cover.
  const summary = loading || accounts.length === 0
    ? null
    : `How your ${eligible.length} customers progress from signup to paying${betas > 0 ? ` (${betas} beta account${betas === 1 ? "" : "s"} excluded)` : ""}.`;
  useEffect(() => { onSummary(summary); }, [summary, onSummary]);
  useEffect(() => () => onSummary(null), [onSummary]);

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }
  if (accounts.length === 0) {
    return (
      <div className="space-y-5">
        <Card>
          <Empty icon={Filter} title="No customers yet">
            The funnel fills in automatically as people sign up for SyncSuite.
          </Empty>
        </Card>
        <OfferLadder />
      </div>
    );
  }

  const payers = eligible.filter((a) => Number(a.revenue_usd) > 0);

  const odds = computeMeasuredOdds(accounts);
  let collectingUsd = 0;
  let potentialUsd = 0;
  for (const a of accounts) {
    const v = monthlyValueUsd(a, odds);
    if (v.weighted) potentialUsd += v.usd;
    else collectingUsd += v.usd;
  }
  const trialers = eligible.filter(
    (a) => a.subscription_status === "trialing" && isNearTrial(a.trial_ends_at),
  );
  const ifTrialsConvert = trialers.reduce(
    (n, a) => n + (PLAN_MONTHLY_USD[a.plan_code ?? ""] || PLAN_MONTHLY_USD.pro!),
    0,
  );
  // Every account's Lifetime number (plan payments + wallet top-ups, the
  // same figure shown on its account page) added up, betas included,
  // since their top-ups are real collected dollars.
  const lifetimeUsd = accounts.reduce((n, a) => n + lifetimeTotal(a), 0);
  const lifetimeAccounts = accounts.filter((a) => lifetimeTotal(a) > 0).length;

  const base = eligible.length || 1;
  const rows = STEPS.map((step) => {
    const reachedAccounts = eligible.filter(step.reached);
    const n = reachedAccounts.length;
    const paidOfReached = reachedAccounts.filter((a) => Number(a.revenue_usd) > 0).length;
    return {
      ...step,
      n,
      shareOfAll: n / base,
      payRate: n > 0 ? paidOfReached / n : null,
      paidOfReached,
    };
  });

  return (
    <div className="space-y-5">
      {/* Money headline */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MoneyCard
          label="Lifetime profit"
          value={usd(lifetimeUsd)}
          tone="text-teal-300"
          sub={`all plan payments + wallet top-ups, from ${lifetimeAccounts} account${lifetimeAccounts === 1 ? "" : "s"}`}
          glow
        />
        <MoneyCard
          label="Collecting now"
          value={usd(collectingUsd)}
          per
          tone="text-emerald-300"
          sub={`${payers.length} paying customer${payers.length === 1 ? "" : "s"}`}
        />
        <MoneyCard
          label="Weighted potential"
          value={`+${usd(potentialUsd)}`}
          per
          tone="text-violet-300"
          sub="real prices × your measured conversion rates"
        />
        <MoneyCard
          label="If every trial converts"
          value={`+${usd(ifTrialsConvert)}`}
          per
          tone="text-amber-300"
          sub={`${trialers.length} active trial${trialers.length === 1 ? "" : "s"}`}
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        {/* The funnel itself */}
        <Section
          title="Milestones reached"
          icon={Filter}
          action={
            <span className="hidden items-center gap-1 text-[11px] text-zinc-500 sm:inline-flex">
              <TrendingUp className="h-3 w-3" />
              right column: how many of them ended up paying
            </span>
          }
        >
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.key}>
                <div className="mb-1 flex items-baseline justify-between text-xs">
                  <span className="text-zinc-300">{r.label}</span>
                  <span className="tabular-nums text-zinc-500">
                    <span className="font-semibold text-zinc-100">{r.n}</span>
                    <span className="text-zinc-600"> of {eligible.length}</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-6 flex-1 overflow-hidden rounded-md bg-zinc-800/60">
                    <div
                      className={cn("flex h-full items-center rounded-md px-2", r.key === "paying" ? "bg-emerald-500/70" : "cc-brand-bg opacity-90")}
                      style={{ width: `${Math.max(r.shareOfAll * 100, 6)}%` }}
                    >
                      <span className="text-[10px] font-semibold tabular-nums text-white/90">
                        {Math.round(r.shareOfAll * 100)}%
                      </span>
                    </div>
                  </div>
                  <div className="w-24 shrink-0 text-right text-[11px] tabular-nums sm:w-28">
                    {r.key === "paying" ? (
                      <span className="text-zinc-600">–</span>
                    ) : r.payRate != null ? (
                      <span className={r.payRate >= 0.5 ? "text-emerald-300" : "text-zinc-400"}>
                        {r.paidOfReached} paid ({Math.round(r.payRate * 100)}%)
                      </span>
                    ) : (
                      <span className="text-zinc-600">no data</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] leading-snug text-zinc-500">
            Bars show the share of all accounts that ever reached each milestone
            (steps can happen out of order, so bars don't have to shrink). The
            paid-rate column feeds the weighted-potential math on the{" "}
            <button type="button" onClick={onShowBoard} className="cursor-pointer text-indigo-300 hover:underline">Board view</button>.
            Everything recomputes from live data on every visit.
          </p>
        </Section>

        <OfferLadder />
      </div>
    </div>
  );
}

function MoneyCard({ label, value, sub, tone, per, glow }: {
  label: string;
  value: string;
  sub: string;
  tone: string;
  per?: boolean;
  glow?: boolean;
}) {
  return (
    <Card className={cn("h-full p-4", glow && "cc-glow")}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{label}</div>
      <div className={cn("mt-2 text-2xl font-semibold tabular-nums tracking-tight", tone)}>
        {value}{per && <span className="text-sm font-medium text-zinc-500">/mo</span>}
      </div>
      <div className="mt-1 text-xs text-zinc-500">{sub}</div>
    </Card>
  );
}
